import { List, MapPin, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { GLYPH } from '../page/SitesCard';
import { type CatalogSites, listSupplierSites, type MapSite, useLoaded } from './api';
import { type CatalogFilters, monthLabel, type SiteCategory } from './list-state';

// The few pieces of the Maps JavaScript API this view uses. No `@types/google.maps`: the
// surface is small, and the loader below is the only door to it.
type LatLng = { readonly lat: number; readonly lng: number };
interface MapsListener {
  remove(): void;
}
interface GMap {
  fitBounds(bounds: GBounds, padding?: number): void;
  panTo(position: LatLng): void;
  setZoom(zoom: number): void;
  getZoom(): number | undefined;
}
interface GBounds {
  extend(position: LatLng): void;
}
interface GMarker {
  addListener(event: 'click', handler: () => void): MapsListener;
  setMap(map: GMap | null): void;
  setIcon(icon: unknown): void;
  setZIndex(zIndex: number): void;
}
export interface MapsApi {
  readonly Map: new (element: HTMLElement, options: Record<string, unknown>) => GMap;
  readonly Marker: new (options: Record<string, unknown>) => GMarker;
  readonly LatLngBounds: new () => GBounds;
}
type Bootstrap = {
  importLibrary?: (name: string) => Promise<Record<string, unknown>>;
} & Partial<MapsApi>;

declare global {
  interface Window {
    google?: { maps?: Bootstrap };
    __dgbMapsReady?: () => void;
    gm_authFailure?: () => void;
  }
}

/**
 * The browser key (tools/gcp/README.md, «What the app will need»); empty where none is set.
 *
 * CSP: `firebase.json`'s report-only policy names the exact Maps hosts this view needs
 * (maps.googleapis.com and maps.gstatic.com for script, connect and images; fonts.googleapis.com
 * and fonts.gstatic.com for the map's styles and fonts; `blob:` workers). Before that policy is
 * enforced, read its reports on staging and tighten the list to what the map actually loaded:
 * Google's own allowlist is broader (wildcards, `*.google.com`, `*.ggpht.com`).
 */
const BROWSER_KEY = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY ?? '';

let loading: Promise<MapsApi> | null = null;
const authFailures = new Set<() => void>();

async function libraries(bootstrap: Bootstrap): Promise<MapsApi> {
  if (bootstrap.Map && bootstrap.Marker && bootstrap.LatLngBounds) {
    return bootstrap as MapsApi;
  }
  if (typeof bootstrap.importLibrary !== 'function') throw new Error('Maps not loaded');
  const [maps, marker, core] = await Promise.all([
    bootstrap.importLibrary('maps'),
    bootstrap.importLibrary('marker'),
    bootstrap.importLibrary('core'),
  ]);
  return {
    Map: maps['Map'] as MapsApi['Map'],
    Marker: marker['Marker'] as MapsApi['Marker'],
    LatLngBounds: core['LatLngBounds'] as MapsApi['LatLngBounds'],
  };
}

/** Loads the Maps JavaScript API once per page; a failed load may be tried again. */
export function loadMaps(key: string, language: string): Promise<MapsApi> {
  // Google calls this when the key is refused (referrer, quota, billing), even after the load.
  window.gm_authFailure = () => authFailures.forEach((notify) => notify());
  const ready = window.google?.maps;
  if (ready) return libraries(ready);
  if (loading) return loading;
  loading = new Promise<Bootstrap>((resolve, reject) => {
    window.__dgbMapsReady = () => {
      const maps = window.google?.maps;
      if (maps) resolve(maps);
      else reject(new Error('Maps not loaded'));
    };
    const script = document.createElement('script');
    const query = new URLSearchParams({
      key,
      v: 'weekly',
      loading: 'async',
      language,
      callback: '__dgbMapsReady',
    });
    script.src = `https://maps.googleapis.com/maps/api/js?${query.toString()}`;
    script.async = true;
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error('Maps did not load'));
    };
    document.head.appendChild(script);
  }).then(libraries);
  return loading;
}

const glyphs = new Map<SiteCategory | 'none', string>();

/**
 * The types' glyphs as SVG markup, drawn once, outside React's effects (where `flushSync` would
 * not run synchronously): the same Lucide glyphs as the list and the supplier page.
 */
function drawGlyphs(): void {
  if (glyphs.size > 0) return;
  const entries: [SiteCategory | 'none', typeof MapPin][] = [
    ...(Object.entries(GLYPH) as [SiteCategory, typeof MapPin][]),
    ['none', MapPin],
  ];
  for (const [category, Glyph] of entries) {
    const holder = document.createElement('div');
    const root = createRoot(holder);
    flushSync(() =>
      root.render(<Glyph size={16} color="currentColor" strokeWidth={2.25} x={8} y={7} />),
    );
    glyphs.set(category, holder.innerHTML);
    root.unmount();
  }
}

/** A pin with the type's glyph (U24: a symbol per type, not a colour alone), as an image. */
function pinIcon(category: SiteCategory | null, selected: boolean): Record<string, unknown> {
  const styles = getComputedStyle(document.documentElement);
  const fill =
    styles.getPropertyValue(selected ? '--selected-foreground' : '--foreground').trim() ||
    '#0b2530';
  const ink = styles.getPropertyValue('--background').trim() || '#ffffff';
  const glyph = (glyphs.get(category ?? 'none') ?? '').replaceAll('currentColor', ink);
  const size = selected ? 40 : 32;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${(size * 5) / 4}" viewBox="0 0 32 40">` +
    `<path d="M16 39c-1-4-13-13-13-23a13 13 0 0 1 26 0c0 10-12 19-13 23z" fill="${fill}" stroke="${ink}" stroke-width="1.5"/>` +
    `${glyph}</svg>`;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}` };
}

/** Google's own dark scheme is a Map ID feature; without one, a short style keeps dark readable. */
const DARK_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#1d2c33' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#c7d3d8' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0f1b20' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2e434c' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e2a38' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
];

type MapStatus = 'no-key' | 'loading' | 'ready' | 'failed';

/**
 * «Mappa» (U24, boards 4.3, 4.5, 4.11): the filtered suppliers' sites with coordinates, one pin per
 * site with its type's glyph, in the list's order (most used first) for the keyboard. A title says
 * what it shows; the same sites as a list sit beside it on a wide screen, and replace it when the
 * map cannot load (no key, Google not answering): the view never depends on the map alone.
 */
export function SuppliersMap({
  filters,
  context,
  onShowList,
  apiKey = BROWSER_KEY,
}: {
  readonly filters: CatalogFilters;
  /** What the filters hold, for the title («Filtri: Paese Italia»). */
  readonly context: string;
  /** Back to «Elenco» with the same filters (the link under the map below 1200 px). */
  readonly onShowList: () => void;
  readonly apiKey?: string;
}) {
  const { t } = useTranslation('suppliers');
  const titleId = useId();
  const key = JSON.stringify(filters);
  const { state, reload } = useLoaded<CatalogSites>(key, () => listSupplierSites(filters));
  const [selected, setSelected] = useState<string | null>(null);
  const [mapStatus, setMapStatus] = useState<MapStatus>(apiKey === '' ? 'no-key' : 'loading');

  if (state.status === 'loading') {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <p className="sr-only" role="status">
          {t('map.loading')}
        </p>
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-[420px] w-full" />
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <StateMessage
        role="alert"
        tone="error"
        icon={MapPin}
        title={
          state.problem.code === 'LIST_FILTER_FORBIDDEN'
            ? (state.problem.message ?? t('list.forbidden'))
            : t('map.loadError')
        }
        action={
          <Button variant="outline" onClick={reload}>
            {t('list.retry')}
          </Button>
        }
      />
    );
  }

  const sites = state.items;
  const suppliers = new Set(sites.map((site) => site.supplierId)).size;
  const mapShown = mapStatus === 'loading' || mapStatus === 'ready';
  const current = sites.find((site) => site.siteId === selected) ?? null;

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-3">
      <div>
        <h3 id={titleId} className="text-lg font-bold">
          {t('map.title', {
            sites: t('map.sites', { count: sites.length }),
            suppliers: t('map.suppliers', { count: suppliers }),
          })}{' '}
          · {context}
        </h3>
        <p className="text-muted-foreground text-sm">
          {t('map.text')}
          {state.unmapped > 0 ? ` ${t('map.unmapped', { count: state.unmapped })}` : ''}
          {mapShown ? (
            <span className="@max-[619px]/shell:hidden"> {t('map.keyboard')}</span>
          ) : null}
        </p>
        {state.truncated ? <p className="text-sm">{t('map.truncated')}</p> : null}
      </div>
      {mapStatus === 'no-key' || mapStatus === 'failed' ? (
        <p role="status" className="border-border bg-muted/50 rounded-lg border p-3 text-sm">
          {t(mapStatus === 'no-key' ? 'map.noKey' : 'map.loadFailed')}
        </p>
      ) : null}
      {sites.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('map.empty')}</p>
      ) : (
        <div
          className={cn(
            'grid gap-3',
            mapShown && '@min-[1200px]/shell:grid-cols-[minmax(0,1fr)_340px]',
          )}
        >
          {mapShown ? (
            <MapCanvas
              apiKey={apiKey}
              sites={sites}
              selected={current}
              onSelect={setSelected}
              onStatus={setMapStatus}
            />
          ) : null}
          <SiteList
            sites={sites}
            selected={selected}
            onSelect={mapStatus === 'ready' ? setSelected : null}
            className={mapShown ? 'hidden @min-[1200px]/shell:flex' : 'flex'}
          />
        </div>
      )}
      {mapShown && sites.length > 0 ? (
        <Button
          variant="ghost"
          className="min-h-11 self-start @min-[1200px]/shell:hidden"
          onClick={onShowList}
        >
          <List />
          {t('map.asList')}
        </Button>
      ) : null}
    </section>
  );
}

function MapCanvas({
  apiKey,
  sites,
  selected,
  onSelect,
  onStatus,
}: {
  readonly apiKey: string;
  readonly sites: readonly MapSite[];
  readonly selected: MapSite | null;
  readonly onSelect: (siteId: string | null) => void;
  readonly onStatus: (status: MapStatus) => void;
}) {
  const { t, i18n } = useTranslation('suppliers');
  const element = useRef<HTMLDivElement>(null);
  const [api, setApi] = useState<{ maps: MapsApi; map: GMap } | null>(null);
  const markers = useRef(new Map<string, GMarker>());
  const language = i18n.resolvedLanguage ?? 'it';

  // The map, once.
  useEffect(() => {
    let live = true;
    const fail = () => {
      if (live) onStatus('failed');
    };
    authFailures.add(fail);
    loadMaps(apiKey, language)
      .then((maps) => {
        if (!live || element.current === null) return;
        drawGlyphs();
        const dark = document.documentElement.classList.contains('dark');
        const map = new maps.Map(element.current, {
          center: { lat: 45.4, lng: 11.9 },
          zoom: 6,
          clickableIcons: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          ...(dark ? { styles: DARK_STYLE, backgroundColor: '#1d2c33' } : {}),
        });
        setApi({ maps, map });
        onStatus('ready');
      })
      .catch(fail);
    return () => {
      live = false;
      authFailures.delete(fail);
    };
    // The key and the language do not change while the page is open.
  }, []);

  // The pins, in the list's order, and the view that holds them all.
  useEffect(() => {
    if (api === null) return;
    const { maps, map } = api;
    const bounds = new maps.LatLngBounds();
    const listeners: MapsListener[] = [];
    const placed = new Map<string, GMarker>();
    sites.forEach((site, index) => {
      const position = { lat: site.latitude, lng: site.longitude };
      bounds.extend(position);
      const marker = new maps.Marker({
        map,
        position,
        title: `${site.name}${site.city ? `, ${site.city}` : ''}`,
        icon: pinIcon(site.category, false),
        zIndex: sites.length - index,
        optimized: false,
      });
      listeners.push(marker.addListener('click', () => onSelect(site.siteId)));
      placed.set(site.siteId, marker);
    });
    markers.current = placed;
    if (sites.length === 1 && sites[0]) {
      map.panTo({ lat: sites[0].latitude, lng: sites[0].longitude });
      map.setZoom(15);
    } else if (sites.length > 1) {
      map.fitBounds(bounds, 48);
    }
    return () => {
      listeners.forEach((listener) => listener.remove());
      placed.forEach((marker) => marker.setMap(null));
      markers.current = new Map();
    };
  }, [api, sites, onSelect]);

  // The chosen site: its pin stands out and the map moves to it.
  useEffect(() => {
    if (api === null || selected === null) return;
    const marker = markers.current.get(selected.siteId);
    if (!marker) return;
    marker.setIcon(pinIcon(selected.category, true));
    marker.setZIndex(sites.length + 1);
    api.map.panTo({ lat: selected.latitude, lng: selected.longitude });
    if ((api.map.getZoom() ?? 0) < 13) api.map.setZoom(13);
    const index = sites.indexOf(selected);
    return () => {
      marker.setIcon(pinIcon(selected.category, false));
      marker.setZIndex(sites.length - index);
    };
  }, [api, selected, sites]);

  return (
    <div
      role="region"
      aria-label={t('map.region')}
      className="border-border relative h-[420px] overflow-hidden rounded-xl border @min-[1200px]/shell:h-[560px]"
    >
      <div ref={element} className="bg-muted absolute inset-0" />
      {selected ? <SiteCard site={selected} onClose={() => onSelect(null)} /> : null}
    </div>
  );
}

/** The card a pin opens (board 4.3): the site, its supplier, the uses; from there, the page. */
function SiteCard({ site, onClose }: { readonly site: MapSite; readonly onClose: () => void }) {
  const { t, i18n } = useTranslation('suppliers');
  const titleId = useId();
  const Glyph = site.category ? GLYPH[site.category] : MapPin;
  const month = monthLabel(site.lastUsed, i18n.resolvedLanguage);
  return (
    <div
      role="dialog"
      aria-labelledby={titleId}
      className="border-border bg-card text-card-foreground absolute right-3 bottom-3 left-3 z-10 flex flex-col gap-1 rounded-xl border p-3 shadow-lg @min-[620px]/shell:right-auto @min-[620px]/shell:w-[320px]"
    >
      <div className="flex items-center gap-2">
        {site.category ? (
          <span className="text-muted-foreground inline-flex items-center gap-1 text-sm">
            <Glyph aria-hidden="true" className="size-4" />
            {t(`category.${site.category}`)}
          </span>
        ) : null}
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto"
          aria-label={t('map.close')}
          onClick={onClose}
        >
          <X />
        </Button>
      </div>
      <b id={titleId}>{site.name}</b>
      <small className="text-muted-foreground">
        {site.name !== site.supplierName
          ? `${t('map.siteOf', { supplier: site.supplierName })}`
          : null}
        {site.name !== site.supplierName && site.city ? ' · ' : null}
        {site.city}
      </small>
      <small className="text-muted-foreground">
        {t('list.uses', { count: site.uses })}
        {month ? ` · ${t('map.lastUsed', { month })}` : null}
      </small>
      <Button asChild size="sm" className="mt-1 self-start">
        <Link to={`/expenses/suppliers/${encodeURIComponent(site.supplierId)}`}>
          {t('map.openSupplier')}
        </Link>
      </Button>
    </div>
  );
}

/** The map's text alternative (U24): the same sites, in the same order, as a list. */
function SiteList({
  sites,
  selected,
  onSelect,
  className,
}: {
  readonly sites: readonly MapSite[];
  readonly selected: string | null;
  readonly onSelect: ((siteId: string) => void) | null;
  readonly className: string;
}) {
  const { t } = useTranslation('suppliers');
  const headingId = useId();
  return (
    <div
      className={cn(
        'border-border max-h-[560px] flex-col overflow-hidden rounded-xl border',
        className,
      )}
    >
      <div
        id={headingId}
        className="bg-muted/50 text-muted-foreground border-border border-b px-3 py-2 text-xs font-semibold tracking-wide uppercase"
      >
        {t('map.listTitle')}
      </div>
      <ol aria-labelledby={headingId} className="flex flex-col overflow-y-auto">
        {sites.map((site) => {
          const Glyph = site.category ? GLYPH[site.category] : MapPin;
          const on = site.siteId === selected;
          const where = [
            site.name !== site.supplierName
              ? t('map.siteOf', { supplier: site.supplierName })
              : null,
            site.city,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <li
              key={site.siteId}
              className={cn(
                'border-border flex items-start gap-3 border-b px-3 py-2.5 last:border-b-0',
                on && 'bg-selected text-selected-foreground',
              )}
            >
              <Glyph aria-hidden="true" className="text-muted-foreground mt-0.5 size-4 shrink-0" />
              <span className="flex min-w-0 flex-1 flex-col">
                {onSelect ? (
                  <button
                    type="button"
                    className="text-left font-semibold hover:underline"
                    aria-pressed={on}
                    aria-label={t('map.show', { name: site.name })}
                    onClick={() => onSelect(site.siteId)}
                  >
                    {site.name}
                  </button>
                ) : (
                  <Link
                    to={`/expenses/suppliers/${encodeURIComponent(site.supplierId)}`}
                    className="font-semibold hover:underline"
                  >
                    {site.name}
                  </Link>
                )}
                {where ? <small className="text-muted-foreground">{where}</small> : null}
              </span>
              <span className="text-muted-foreground text-sm whitespace-nowrap tabular-nums">
                {t('list.uses', { count: site.uses })}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
